import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { message } from 'antd';
import DataTable from '../../reusable/DataTable/DataTable';
import SymbolFlag from '../../reusable/SymbolFlag/SymbolFlag';
import StatusChip from '../../reusable/StatusChip/StatusChip';
import SegmentedTabs from '../../reusable/SegmentedTabs/SegmentedTabs';
import CandlestickChart from '../../reusable/CandlestickChart/CandlestickChart';
import LineChart from '../../reusable/LineChart/LineChart';
import ChartLegend from '../../reusable/ChartLegend/ChartLegend';
import ExportDialog from '../../reusable/ExportDialog/ExportDialog';
import Button from '../../reusable/Button/Button';
import CreateJournalDialog from '../Trademate/CreateJournalDialog';
import { CHART_COLORS } from '../../reusable/charts/chartTheme';
import { NA, signedUsd } from '../../utilities/format';
import { formatLoginId } from '../../utilities/accounts';
import { closedTrades, rowKeys } from '../../utilities/deals';
import { debounce } from '../../utilities/debounce';
import { saveBlob } from '../../utilities/download';
import { useGetAccountsQuery } from '../../store/api/dashboardApi';
import { selectAccount, selectPickedAccount } from '../../store/slices/accountSlice';
import {
  useGetTradeHistoryQuery,
  useGetAllTradesQuery,
  useLazyExportTradeHistoryQuery,
} from '../../store/api/tradeHistoryApi';
import { useGetCandlesQuery } from '../../store/api/tickdataApi';
import { TIMEFRAME_SECONDS, aggregateCandles, candleRequest, indexAt } from './candles';
import ChartCardSkeleton from './TradeHistorySkeleton';
import ColumnPicker from './ColumnPicker';
import { t } from '../../i18n/i18n';
import './TradeHistory.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** How far back each range tab looks, in days. "All" keeps everything. */
const RANGE_DAYS = { All: null, Today: 1, '7 days': 7, '30 days': 30 };

const PAGE_SIZE = 10;
/** Offered in the pager; the chosen one is the request's own `page_size`. */
const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];
const SEARCH_DELAY = 300;

const filename = (login, { start_date: from, end_date: to, filter } = {}) =>
  `trades${login ? `-${login}` : ''}${filter ? `-${filter}` : ''}${from ? `-${from}` : ''}${to ? `-${to}` : ''}.csv`;

/** The `filter` the endpoint takes; "All" is the param left off. */
const FILTER_OPTIONS = [
  { label: 'All trades', value: null },
  { label: 'Winners', value: 'winners' },
  { label: 'Losers', value: 'losers' },
];

/**
 * A range tab as the endpoint's `start_date` / `end_date`. Local dates, not
 * `toISOString`, which would hand over yesterday for anyone west of UTC.
 */
const rangeDates = (label) => {
  const days = RANGE_DAYS[label];
  if (!days) return {};
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { start_date: format(start, 'yyyy-MM-dd'), end_date: format(end, 'yyyy-MM-dd') };
};

const ALL_TRADES = 'All trades';
const SELECTED_TRADE = 'Selected trade';
const CHART_MODES = [ALL_TRADES, SELECTED_TRADE];
const TIMEFRAMES = Object.keys(TIMEFRAME_SECONDS);

/**
 * The "All trades" view: the running P&L of the trades on this page, oldest
 * first, one point per close — the shape of the page at a glance before any
 * one trade is picked.
 */
const cumulativePnl = (trades) => {
  let running = 0;
  return [...trades]
    .sort((a, b) => a.closedAt - b.closedAt)
    .map((t) => {
      running = Math.round((running + t.pnl) * 100) / 100;
      return { x: t.closedAt.toISOString(), y: running };
    });
};

const legendItems = [
  { color: CHART_COLORS.down, label: 'Entry' },
  { color: CHART_COLORS.info, label: 'Exit' },
];

/* ---- the table's columns, read off the response ---- */

/** `volumeClosed` → "Volume Closed", `deal_type` → "Deal Type". */
const humanize = (key) =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** The keys humanising alone doesn't make readable. */
const HEADERS = { pnl: 'P&L', netPnl: 'Net P&L', id: 'ID' };

/** The names each platform gives its symbol, its side and its money. */
const SYMBOL_KEYS = new Set(['symbol', 'instrument']);
const SIDE_KEYS = new Set(['action', 'side', 'trade_side']);
const MONEY_KEYS = new Set([
  'profit', 'new_profit', 'net_profit', 'netPnl', 'pnl', 'swap', 'commission', 'executionFee',
]);

/** The names each platform gives the reason a position closed. */
const EXIT_REASON_KEYS = new Set(['exit_reason', 'close_reason', 'closeReason']);

/**
 * An exit reason's tag colour — N44, from the August request (A9): take-profit
 * green, stop-loss red, a manual close grey. A stop-out or margin call is a
 * stop the trader didn't choose, so it reads red with the stop-loss. Anything
 * else the platform reports (an EA close, an expiry) is neutral.
 */
const exitTone = (value) => {
  // Matched as words anywhere, not only at the start, and with MT5's enum
  // prefix dropped — so "TP Hit", "Take profit", "Closed by stop loss" and
  // "DEAL_REASON_SL" all find their colour (TI review N44).
  const text = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .replace(/^deal reason /, '');
  if (/\b(tp|take profit)\b/.test(text)) return 'success';
  if (/\b(sl|stop loss|stop out|so|margin( call)?)\b/.test(text)) return 'danger';
  return 'neutral';
};

/** The exit reason as a tag; an empty one ("N/A", nothing) stays plain text. */
function ExitReason({ value }) {
  const text = value == null ? '' : String(value).trim();
  if (!text || /^n\/?a$/i.test(text)) return NA;
  return <StatusChip tone={exitTone(text)}>{text}</StatusChip>;
}

/** A timestamp the API sends as text — shown as a date, still its own value. */
const TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;

/**
 * The broker's clock, which every open and close time in this table is on —
 * N41. The endpoint sends those times with no zone (`2026-09-23T18:39:44`) and
 * the table prints them as sent, so they are not the viewer's local time: they
 * are the MT5 server's, GMT+2, moving to GMT+3 while US daylight saving runs
 * (second Sunday of March to first Sunday of November) — the same note the
 * Downloads page gives. The live dashboard labels the same raw times with the
 * *viewer's* offset ("Timezone : GMT+0530"), which they are not in.
 */
const brokerOffset = (now = new Date()) => {
  const year = now.getUTCFullYear();
  const nthSunday = (month, n) => {
    const first = new Date(Date.UTC(year, month, 1));
    return Date.UTC(year, month, 1 + ((7 - first.getUTCDay()) % 7) + (n - 1) * 7);
  };
  const dstStart = nthSunday(2, 2); // second Sunday of March
  const dstEnd = nthSunday(10, 1); // first Sunday of November
  return now.getTime() >= dstStart && now.getTime() < dstEnd ? 3 : 2;
};

const brokerTimeNote = () =>
  t(
    'Open and close times are in broker time, GMT+{{offset}} now (GMT+2, or GMT+3 while US daylight saving is on).',
    { offset: brokerOffset() }
  );

/** The payload's own value, printed. Nothing renamed, nothing rounded away. */
const cellText = (value) => {
  if (value == null || value === '') return NA;
  if (typeof value === 'boolean') return value ? t('Yes') : t('No');
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  return TIMESTAMP.test(text) ? format(new Date(text), 'd MMM yyyy HH:mm') : text;
};

const sign = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return undefined;
  return n > 0 ? 'up' : 'down';
};

const sideTone = (value) => {
  const text = String(value ?? '').trim().toUpperCase();
  if (text.startsWith('B')) return 'buy';
  if (text.startsWith('S')) return 'sell';
  return undefined;
};

/**
 * One column per key the response carries, in the order the rows list them —
 * a later row can carry a key the first one left off (a `comment`, a
 * `stopLoss`), so the whole page is read rather than just its first row.
 *
 * The four platforms send four different sets — MT5's `volumeClosed` and
 * `profitRate`, TradeLocker's `netPnl` and `executionFee`, cTrader's
 * `deal_status`, DXtrade's `order_code` — so the columns come off the payload
 * instead of being written out for one platform and guessed at for the rest.
 */
const buildColumns = (rows) => {
  const keys = [];
  for (const row of rows) {
    for (const key of Object.keys(row ?? {})) if (!keys.includes(key)) keys.push(key);
  }

  return keys.map((key) => ({
    key,
    header: HEADERS[key] ?? humanize(key),
    // Every column an even share of the row, with a floor so a long value
    // (a TradeLocker ticket, an ISO date) doesn't squeeze its neighbours.
    minWidth: SYMBOL_KEYS.has(key) ? 140 : 112,
    // The side is shown in the reader's language; `tone` still reads the raw value.
    value: SIDE_KEYS.has(key) ? (row) => t(cellText(row[key])) : (row) => cellText(row[key]),
    render: SYMBOL_KEYS.has(key)
      ? (row) => (
          <span className="th-symbol">
            {/* "AUDCAD.pro" is the broker's symbol; the flag wants "AUDCAD". */}
            <SymbolFlag symbol={String(row[key] ?? '').split('.')[0]} />
            <span className="th-symbol__text">{cellText(row[key])}</span>
          </span>
        )
      : EXIT_REASON_KEYS.has(key)
        ? (row) => <ExitReason value={row[key]} />
        : undefined,
    tone: SIDE_KEYS.has(key)
      ? (row) => sideTone(row[key])
      : MONEY_KEYS.has(key)
        ? (row) => sign(row[key])
        : undefined,
  }));
};

/**
 * The columns the table opens with (TI review N43): the payload carries about
 * twenty fields and the live dashboard showed eight, so the rest are a click
 * away in "Columns" rather than a long scroll sideways. Picked by meaning, as
 * each platform names its fields its own way — the first key of each group the
 * response carries, in the response's own order.
 */
const DEFAULT_COLUMN_GROUPS = [
  ['symbol', 'instrument'],
  ['action', 'side', 'tradeside', 'type', 'dealtype'],
  ['volume', 'volumeclosed', 'lots', 'lot', 'qty', 'quantity'],
  ['opentime', 'opendate', 'timeopen', 'openedat', 'entrytime'],
  ['openprice', 'entryprice', 'priceopen'],
  ['closetime', 'closedate', 'timeclose', 'closedat', 'exittime'],
  ['closeprice', 'exitprice', 'priceclose'],
  ['profit', 'netprofit', 'newprofit', 'netpnl', 'pnl'],
  ['exitreason', 'closereason'],
];
/** `volumeClosed`, `volume_closed` → "volumeclosed". */
const squash = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, '');

const defaultColumnKeys = (keys) => {
  const picked = new Set();
  DEFAULT_COLUMN_GROUPS.forEach((names) => {
    const hit = keys.find((key) => names.includes(squash(key)));
    if (hit) picked.add(hit);
  });
  // A platform whose fields none of this recognises still opens on a table
  // that fits: its first eight.
  return picked.size >= 4 ? keys.filter((key) => picked.has(key)) : keys.slice(0, 8);
};

/** The trader's own choice, per platform — wrapped, as storage can be blocked. */
const columnsKey = (platform) => `th.columns.${platform ?? 'any'}`;
const storedColumns = (platform) => {
  try {
    const list = JSON.parse(localStorage.getItem(columnsKey(platform)));
    return Array.isArray(list) && list.length ? list : null;
  } catch {
    return null;
  }
};
const storeColumns = (platform, list) => {
  try {
    if (list) localStorage.setItem(columnsKey(platform), JSON.stringify(list));
    else localStorage.removeItem(columnsKey(platform));
  } catch {
    // Storage blocked: the choice lasts until the page is left.
  }
};

const RANGES = ['All', 'Today', '7 days', '30 days'];

/**
 * The table's title, following what is on screen — N44: "Today" used to keep
 * the title "All trades" while showing nothing but today. The winners/losers
 * filter names the trades; the range tab says when.
 */
const tableTitle = (filter, range) => {
  const what =
    filter === 'winners'
      ? t('Winning trades')
      : filter === 'losers'
        ? t('Losing trades')
        : t('All trades');
  const when = { Today: 'today', '7 days': 'last 7 days', '30 days': 'last 30 days' }[range];
  if (!when) return what;
  return `${filter ? what : t('Trades')} · ${t(when)}`;
};
  
export default function TradeHistory() {
  /** Search, filter and range are all server-side now, so they share a page. */
  const [query, setQuery] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
    search: '',
    filter: null,
    range: 'All',
  });
  const { range } = query;

  /** Anything but the page number is a new result set, so it lands back on page 1. */
  const setQueryParam = (key, value) => setQuery((prev) => ({ ...prev, page: 1, [key]: value }));
  const setRange = (value) => setQueryParam('range', value);

  const onSearch = useMemo(
    () =>
      debounce(
        (value) => setQuery((prev) => ({ ...prev, page: 1, search: value.trim() })),
        SEARCH_DELAY
      ),
    []
  );

  const [picked, setPicked] = useState(null);
  // Opens on the whole page, not one trade: nothing is picked until a row is
  // clicked, and "Selected trade" over a trade nobody chose read as a bug.
  const [chartMode, setChartMode] = useState(ALL_TRADES);
  const [timeframe, setTimeframe] = useState('M15');

  /**
   * Whose trades these are, resolved exactly as Account Metrics resolves it —
   * Account Metrics' own "View all" is one of the links that lands here, so the
   * two pages have to read `?login=…&platform=…` the same way. The sidebar's
   * nav item arrives with nothing, hence the fall back to the first account on
   * the profile: the deal ledger only comes per account, there is no
   * all-accounts feed.
   */
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();
  const { data: accountList } = useGetAccountsQuery({ page: 1, page_size: 100 });
  const accounts = accountList?.accounts?.results;
  const firstAccount = accounts?.[0];

  const pickedAccount = useSelector(selectPickedAccount);

  const candidateLogin = params.get('login') ?? pickedAccount.loginId;
  const matchedAccount = accounts?.find(
    (row) => String(row.login_id) === String(candidateLogin)
  );
  // An unknown login falls back to the first account — but only once the
  // whole list is in hand: it is the first hundred, and a login past them
  // (from the picker's later pages, N22) isn't unknown, just not loaded here.
  const listIsWhole =
    accounts != null && (accountList?.accounts?.count ?? accounts.length) <= accounts.length;
  // A login past the first hundred is looked up by itself before it's judged
  // (as on Account Metrics): found, it's the account and its platform; not
  // found, it isn't the trader's.
  const lookup = useGetAccountsQuery(
    { page: 1, page_size: 100, search: String(candidateLogin) },
    { skip: !candidateLogin || !accounts || Boolean(matchedAccount) || listIsWhole },
  );
  const knownAccount =
    matchedAccount ??
    lookup.data?.accounts?.results?.find((row) => String(row.login_id) === String(candidateLogin));
  // A login is only ever sent once it is known to be this trader's. The address
  // (or a picker left over from someone else's session on this browser) can
  // name another trader's account, and asking for it was a 401 — so while the
  // list or the lookup is still answering, nothing is asked for at all.
  const isResolving =
    Boolean(candidateLogin) &&
    (!accounts || (!matchedAccount && !listIsWhole && !lookup.isSuccess && !lookup.isError));
  const loginId = knownAccount
    ? String(knownAccount.login_id)
    : isResolving
      ? null
      : firstAccount
        ? String(firstAccount.login_id)
        : null;
  const activeAccount =
    knownAccount ?? (String(firstAccount?.login_id) === loginId ? firstAccount : null);

  /**
   * The login's own account is read before the picker and the first account:
   * `?login=` alone has to resolve the platform *that* account trades on, since
   * the platform now picks which ledger reads the rows — a stale one wouldn't
   * just mislabel the page, it would empty the table.
   */
  // The account's own record before the address (N114) — a stale
  // `platform=` would read the wrong ledger. The address only decides for a
  // login not in the list.
  const platform =
    activeAccount?.platform ??
    params.get('platform') ??
    (String(pickedAccount.loginId) === String(loginId) ? pickedAccount.platform : null) ??
    firstAccount?.platform ??
    null;

  /** A deep link moves the top bar's picker too, the way Account Metrics does. */
  useEffect(() => {
    if (
      loginId &&
      (String(pickedAccount.loginId) !== String(loginId) ||
        (platform && pickedAccount.platform !== platform))
    ) {
      dispatch(selectAccount({ loginId, platform }));
    }
  }, [loginId, platform, pickedAccount.loginId, pickedAccount.platform, dispatch]);

  /** Arrived with a bare path or mismatched login: write what was resolved back. */
  useEffect(() => {
    if (
      loginId &&
      (params.get('login') !== loginId || (platform && params.get('platform') !== platform))
    ) {
      const next = new URLSearchParams(params);
      next.set('login', loginId);
      if (platform) next.set('platform', platform);
      setParams(next, { replace: true });
    }
  }, [loginId, platform, params, setParams]);

  const historyQuery = useMemo(
    () => ({
      loginId,
      platform,
      page: query.page,
      // The pager's own selection, so changing it is one request for the new
      // size rather than the old size re-asked for.
      page_size: query.pageSize,
      search: query.search,
      filter: query.filter,
      ...rangeDates(query.range),
    }),
    [loginId, platform, query],
  );

  const { data, isFetching, isLoading, isError, error } = useGetTradeHistoryQuery(historyQuery, {
    skip: !loginId,
  });

  // Every trade, for the chart: GET /<login>/trade-history/alltrades/. Only for
  // the trader's own resolved account — with none there is nothing to chart.
  const {
    data: allTradesData,
    isFetching: isFetchingAllTrades,
    isLoading: isLoadingAllTrades,
  } = useGetAllTradesQuery(
    { loginId, platform: platform || undefined },
    { skip: !loginId },
  );

  /** The page as it arrived — what the table draws, field for field. */
  const rows = useMemo(() => data?.results ?? [], [data]);
  const allColumns = useMemo(() => buildColumns(rows), [rows]);

  /* Which columns are on: the trader's pick for this platform, else the
     defaults. A pick naming no field this response has falls back too. */
  const [pickedColumns, setPickedColumns] = useState(() => storedColumns(platform));
  useEffect(() => setPickedColumns(storedColumns(platform)), [platform]);
  const visibleKeys = useMemo(() => {
    const keys = allColumns.map((col) => col.key);
    const mine = pickedColumns?.filter((key) => keys.includes(key));
    return new Set(mine?.length ? mine : defaultColumnKeys(keys));
  }, [allColumns, pickedColumns]);
  const columns = useMemo(
    () => allColumns.filter((col) => visibleKeys.has(col.key)),
    [allColumns, visibleKeys],
  );
  const toggleColumn = (key) => {
    const next = new Set(visibleKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    const list = allColumns.map((col) => col.key).filter((k) => next.has(k));
    setPickedColumns(list);
    storeColumns(platform, list);
  };
  const resetColumns = () => {
    setPickedColumns(null);
    storeColumns(platform, null);
  };

  /**
   * The same rows paired into positions, which is what the chart needs: a leg
   * on its own has no window to draw, only one end of one.
   */
  const trades = useMemo(() => closedTrades(data?.results, platform), [data, platform]);

  /**
   * `Pagination` hides itself at zero, so a response without `count` would
   * drop the pager rather than show one page — fall back to what is on screen.
   */
  const total = data?.count ?? rows.length;

  /**
   * An empty page says why in the API's own words ("No position history
   * found.") when it sends any. Without one, a search, filter or range that
   * matched nothing is told apart from an account that has no trades at all —
   * the fixed "no trades on this account" read as if the account were empty.
   */
  const narrowed = Boolean(query.search || query.filter || query.range !== 'All');
  const noRowsMessage =
    t(data?.message) ??
    (narrowed ? t('No trades match your search or filters') : t('No trades on this account'));

  /**
   * `picked` is a position, not a row: clicking either leg of one selects the
   * trade. The highlight goes back on the row the trade is filed under — its
   * closing leg, whose ticket is the trade's own id. Only what was actually
   * clicked counts — no falling back to the newest trade — and only while the
   * chart is on "Selected trade".
   */
  /** Parse list from /alltrades/ endpoint for chart */
  const allTradesList = useMemo(() => {
    if (!allTradesData) return null;
    if (Array.isArray(allTradesData)) return allTradesData;
    if (Array.isArray(allTradesData.results)) return allTradesData.results;
    if (Array.isArray(allTradesData.trades)) return allTradesData.trades;
    if (Array.isArray(allTradesData.data)) return allTradesData.data;
    if (Array.isArray(allTradesData.deals)) return allTradesData.deals;
    if (Array.isArray(allTradesData.history)) return allTradesData.history;
    return [];
  }, [allTradesData]);

  /** Convert allTrades into closed positions for charting */
  const allClosedTrades = useMemo(() => {
    if (!allTradesList || allTradesList.length === 0) return [];
    return closedTrades(allTradesList, platform);
  }, [allTradesList, platform]);

  const allPnlPoints = useMemo(() => {
    if (allClosedTrades.length > 0) {
      return cumulativePnl(allClosedTrades);
    }
    if (allTradesList && allTradesList.length > 0) {
      if (allTradesList[0]?.x !== undefined && allTradesList[0]?.y !== undefined) {
        return allTradesList;
      }
      if (
        allTradesList[0]?.pnl !== undefined ||
        allTradesList[0]?.profit !== undefined ||
        allTradesList[0]?.net_profit !== undefined
      ) {
        let running = 0;
        return [...allTradesList]
          .map((item, idx) => {
            const val = Number(item.pnl ?? item.profit ?? item.net_profit ?? 0) || 0;
            const timeRaw =
              item.closedAt ??
              item.time ??
              item.date ??
              item.close_time ??
              item.closed_at ??
              item.timestamp;
            const d = timeRaw ? new Date(timeRaw) : new Date(Date.now() + idx * 1000);
            return { at: !Number.isNaN(d.getTime()) ? d : new Date(Date.now() + idx * 1000), val };
          })
          .sort((a, b) => a.at - b.at)
          .map((t) => {
            running = Math.round((running + t.val) * 100) / 100;
            return { x: t.at.toISOString(), y: running };
          });
      }
    }
    return [];
  }, [allClosedTrades, allTradesList]);

  const showingAll = chartMode === ALL_TRADES;
  const selectedTrade = showingAll
    ? null
    : allClosedTrades.find((t) => String(t.position) === String(picked)) ??
      trades.find((t) => String(t.position) === String(picked)) ??
      null;
  const selected = selectedTrade?.id ?? null;

  const pnlPoints = useMemo(() => cumulativePnl(trades), [trades]);

  const activePnlPoints = useMemo(() => {
    if (allPnlPoints.length > 0) return allPnlPoints;
    return pnlPoints;
  }, [allPnlPoints, pnlPoints]);

  const activeTotalPnl = activePnlPoints.at(-1)?.y ?? 0;

  const allTradesCount =
    allClosedTrades.length > 0
      ? allClosedTrades.length
      : allTradesList && allTradesList.length > 0
        ? allTradesList.length
        : trades.length;

  /** Tick candles for the selected trade's own symbol and window. */
  const request = useMemo(() => candleRequest(selectedTrade), [selectedTrade]);
  const candles = useGetCandlesQuery(request ?? {}, { skip: !request });

  /** One candle per timeframe bucket — the tabs are the bucket size. */
  const chartData = useMemo(
    () => aggregateCandles(candles.data?.candles, TIMEFRAME_SECONDS[timeframe]),
    [candles.data, timeframe],
  );
  const entryIndex = indexAt(chartData, selectedTrade?.openedAt);
  const exitIndex = indexAt(chartData, selectedTrade?.closedAt);

  const [exporting, setExporting] = useState(false);
  // N76 — live's "+ Add Trade" on its Trading History tab.
  const [journaling, setJournaling] = useState(false);
  const [runExport, { isFetching: isExporting }] = useLazyExportTradeHistoryQuery();

  const exportTrades = async (range) => {
    if (!loginId) {
      message.warning(t('Open an account from the Dashboard to see its trades'));
      return;
    }

    try {
      const blob = await runExport({
        loginId,
        platform: platform || undefined,
        start_date: range.start_date || undefined,
        end_date: range.end_date || undefined,
        filter: range.status || undefined,
      }).unwrap();

      saveBlob(blob, filename(loginId, range));
      setExporting(false);
    } catch {
      message.error(t('Could not export your trades'));
    }
  };

  const candleNote = !selectedTrade
    ? t('Click a trade in the table to see its price action.')
    : candles.isFetching
      ? t('Loading candles…')
      : candles.isError
        ? t('Could not load candles for {{symbol}} ({{status}}).', {
            symbol: selectedTrade.symbol,
            status: candles.error?.status ?? t('error'),
          })
        : chartData.length === 0
          ? t('No tick data came back for {{symbol}} over this window.', {
              symbol: selectedTrade.symbol,
            })
          : null;

  return (
    <div className="trade-history">
      {/* ---- trade chart — /trade-history/ picks the trade, /candles/ draws it ---- */}
      {isLoading ? <ChartCardSkeleton /> : (
      <section className="th-chart-card">
        <header className="th-chart-card__head">
          <div className="th-chart-card__title">
            <span className="icon th-chart-card__title-icon" style={icon('trade-history-trend')} />
            {showingAll
              ? t('Trade history — All trades')
              : t('Trade history — {{symbol}} {{timeframe}}', {
                  symbol: selectedTrade?.symbol ?? NA,
                  timeframe,
                })}
            {loginId && (
              <span className="th-chart-card__account">{formatLoginId(loginId)}</span>
            )}
          </div>

          <div className="th-chart-card__controls">
            <SegmentedTabs
              radius="track"
              options={CHART_MODES.map((v) => ({ value: v, label: t(v) }))}
              value={chartMode}
              onChange={setChartMode}
            />
            {/* Candle buckets only mean something for one trade's price action. */}
            {!showingAll && (
              <SegmentedTabs radius="track" options={TIMEFRAMES} value={timeframe} onChange={setTimeframe} />
            )}
          </div>
        </header>

        <div className="th-chart-card__summary">
          {showingAll ? (
            <div className="th-chart-card__symbol">
              <span className="th-chart-card__name">
                {allTradesCount === 1
                  ? t('1 closed trade')
                  : t('{{count}} closed trades', { count: allTradesCount })}
              </span>
            </div>
          ) : (
            <div className="th-chart-card__symbol">
              <SymbolFlag symbol={selectedTrade?.symbol ?? ''} size={24} />
              <span className="th-chart-card__name">
                {selectedTrade
                  ? t('{{symbol}} · {{side}} · {{lots}} lots', {
                      symbol: selectedTrade.symbol,
                      side: t(selectedTrade.side),
                      lots: selectedTrade.lots.toFixed(2),
                    })
                  : t('No trade selected')}
              </span>
            </div>
          )}

          {showingAll && activePnlPoints.length > 0 && (
            <div className="th-chart-card__pnl-group">
              <span className={`th-chart-card__pnl ${activeTotalPnl >= 0 ? 'is-up' : 'is-down'}`}>
                {signedUsd(activeTotalPnl, { decimals: 2 })}
              </span>
              <span className="th-chart-card__meta">· {t('running P&L, oldest to newest')}</span>
            </div>
          )}

          {selectedTrade && (
            <div className="th-chart-card__pnl-group">
              <span className={`th-chart-card__pnl ${selectedTrade.pnl >= 0 ? 'is-up' : 'is-down'}`}>
                {signedUsd(selectedTrade.pnl, { decimals: 2 })}
              </span>
              <span className="th-chart-card__meta">
                · #{selectedTrade.id} · {selectedTrade.date} · {selectedTrade.open} →{' '}
                {selectedTrade.close}
              </span>
            </div>
          )}
        </div>

        {showingAll ? (
          isLoadingAllTrades && !allTradesData ? (
            <div className="th-chart-card__empty">{t('Loading all trades…')}</div>
          ) : activePnlPoints.length > 0 ? (
            <LineChart
              data={activePnlPoints}
              lineColor={activeTotalPnl >= 0 ? CHART_COLORS.up : CHART_COLORS.down}
              highlightedIndex={activePnlPoints.length - 1}
              height={224}
            />
          ) : (
            <div className="th-chart-card__empty">
              {isFetching || isFetchingAllTrades
                ? t('Loading trades…')
                : t('No closed trades to chart.')}
            </div>
          )
        ) : candleNote ? (
          <div className="th-chart-card__empty">{candleNote}</div>
        ) : (
          <>
            <CandlestickChart
              data={chartData}
              highlightRange={
                entryIndex >= 0 && exitIndex >= 0
                  ? { startIndex: entryIndex, endIndex: exitIndex }
                  : undefined
              }
              boundaryLines={[
                entryIndex >= 0 && { index: entryIndex, color: CHART_COLORS.down },
                exitIndex >= 0 && { index: exitIndex, color: CHART_COLORS.info },
              ].filter(Boolean)}
              markers={[
                entryIndex >= 0 && { index: entryIndex, type: 'entry' },
                exitIndex >= 0 && { index: exitIndex, type: 'exit' },
              ].filter(Boolean)}
              tooltipFormatter={(c) =>
                `${format(c.at, 'd MMM HH:mm')}  O ${c.open}  H ${c.high}  L ${c.low}  C ${c.close}`
              }
              showGridlines
              showXAxis={false}
              showYAxis={false}
            />

            <ChartLegend
              items={legendItems.map((item) => ({ ...item, label: t(item.label) }))}
              className="th-chart-card__legend"
            />
          </>
        )}
      </section>
      )}

      <DataTable
        title={tableTitle(query.filter, range)}
        subtitle={brokerTimeNote()}
        count={`${total}`}
        columns={columns}
        rows={rows}
        getRowId={(row, i) => rowKeys(row, platform).id ?? i}
        // The columns switched on (see "Columns"); with more than fit, the row
        // scrolls sideways rather than crushing itself to fit.
        minWidth={Math.max(1080, columns.length * 124)}
        // Server-side: `total` hands paging, search and filtering to the API.
        total={total}
        page={query.page}
        // The second argument is the size the pager just switched to.
        onPageChange={(page, size) =>
          setQuery((prev) => ({ ...prev, page, pageSize: size ?? prev.pageSize }))
        }
        /* Ours, not the response's echo: the echo is still the previous size
           while the new request is in flight, which would snap the selector
           back for a moment on every change. */
        pageSize={query.pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        loading={isFetching}
        tabs={{
          options: RANGES.map((v) => ({ value: v, label: t(v) })),
          value: range,
          onChange: setRange,
        }}
        searchPlaceholder={t('Search trades')}
        onSearch={onSearch}
        filter={{
          options: FILTER_OPTIONS.map((o) => ({ ...o, label: t(o.label) })),
          value: query.filter,
          onChange: (value) => setQueryParam('filter', value),
        }}
        onExport={() => setExporting(true)}
        actions={
          <>
            {allColumns.length > 0 && (
              <ColumnPicker
                columns={allColumns}
                visible={visibleKeys}
                onToggle={toggleColumn}
                onReset={resetColumns}
              />
            )}
            <Button
              variant="toolbar"
              icon={<span className="icon" style={icon('affiliate-plus')} />}
              onClick={() => setJournaling(true)}
            >
              {t('Add Trade')}
            </Button>
          </>
        }
        selectedId={selected}
        // Clicking a trade is choosing one, so the chart follows it.
        onRowClick={(row) => {
          setPicked(rowKeys(row, platform).position);
          setChartMode(SELECTED_TRADE);
        }}
        emptyMessage={
          !loginId
            ? t('Open an account from the Dashboard to see its trades')
            : isError
              ? t("Could not load this account's trades ({{status}})", {
                  status: error?.status ?? t('error'),
                })
              : noRowsMessage
        }
      />

      <ExportDialog
        open={exporting}
        title={t('Export trades')}
        statusOptions={FILTER_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))}
        busy={isExporting}
        onExport={exportTrades}
        onClose={() => setExporting(false)}
      />

      <CreateJournalDialog open={journaling} onClose={() => setJournaling(false)} />
    </div>
  );
}
